// src/modules/components/ContactFormModal/index.js
import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaTimes, FaUser, FaEnvelope, FaPhone, FaBuilding, FaFileAlt, FaPaperPlane, FaCheck } from 'react-icons/fa';
import { useTranslation } from '../../../contexts/LanguageContext';
import { supabase } from '../../../config/supabase';

// Emails de admin que não devem ser salvos como leads
const ADMIN_EMAILS = [
  'contato@vegarobotics.com.br',
  'admin@vegarobotics.com.br',
  'vega@vegarobotics.com.br',
  'rafaelmdeveloper@gmail.com',
  'vegarobotics.mkt@gmail.com'
];

// ===== CORES =====
const colors = {
  background: '#0D0D0D',
  backgroundCard: '#161616',
  primary: '#333333',
  primaryDark: '#2A2A2A',
  success: '#2ECC71',
  error: '#E74C3C',
  white: '#FFFFFF',
  textPrimary: '#F5F5F5',
  textSecondary: '#B8B8B8',
  textMuted: '#888888',
  border: 'rgba(255, 255, 255, 0.1)',
};

// ===== ANIMAÇÕES =====
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { 
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to { 
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const checkmark = keyframes`
  0% { transform: scale(0); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
`;

// ===== STYLED COMPONENTS =====
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  animation: ${fadeIn} 0.3s ease;
`;

const Modal = styled.div`
  background: ${colors.backgroundCard};
  border: 1px solid ${colors.border};
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  animation: ${slideUp} 0.4s ease;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${colors.background};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${colors.primary};
    border-radius: 3px;
  }
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: ${colors.backgroundCard};
  z-index: 10;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${colors.textPrimary};
  margin: 0;
  
  span {
    color: ${colors.primary};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${colors.textMuted};
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  
  &:hover {
    color: ${colors.textPrimary};
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${colors.textSecondary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: ${colors.primary};
    font-size: 0.9rem;
  }
`;

const Input = styled.input`
  background: ${colors.background};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: 0.85rem 1rem;
  font-size: 0.95rem;
  color: ${colors.textPrimary};
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px rgba(51, 51, 51, 0.25);
  }
  
  &::placeholder {
    color: ${colors.textMuted};
  }
`;

const TextArea = styled.textarea`
  background: ${colors.background};
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: 0.85rem 1rem;
  font-size: 0.95rem;
  color: ${colors.textPrimary};
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px rgba(51, 51, 51, 0.25);
  }
  
  &::placeholder {
    color: ${colors.textMuted};
  }
`;

const SubmitButton = styled.button`
  background: ${colors.primary};
  color: ${colors.white};
  border: none;
  border-radius: 8px;
  padding: 1rem 1.5rem;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  margin-top: 0.5rem;
  
  &:hover:not(:disabled) {
    background: ${colors.primaryDark};
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(51, 51, 51, 0.35);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${colors.white};
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const SuccessMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  gap: 1rem;
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  background: ${colors.success};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${checkmark} 0.5s ease;
  
  svg {
    color: ${colors.white};
    font-size: 2.5rem;
  }
`;

const SuccessTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${colors.textPrimary};
  margin: 0;
`;

const SuccessText = styled.p`
  font-size: 0.95rem;
  color: ${colors.textSecondary};
  margin: 0;
  line-height: 1.5;
`;

const ErrorMessage = styled.p`
  color: ${colors.error};
  font-size: 0.85rem;
  margin: 0;
  padding: 0.75rem;
  background: rgba(231, 76, 60, 0.1);
  border-radius: 8px;
  text-align: center;
`;

const PrivacyText = styled.p`
  font-size: 0.75rem;
  color: ${colors.textMuted};
  text-align: center;
  margin: 0.5rem 0 0 0;
  line-height: 1.4;
`;

// ===== COMPONENTE =====
const ContactFormModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    empresa: '',
    mensagem: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  // Fecha modal com ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Reset ao fechar
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setFormData({ nome: '', email: '', telefone: '', empresa: '', mensagem: '' });
        setIsSuccess(false);
        setError('');
      }, 300);
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validação básica
    if (!formData.nome || !formData.email || !formData.telefone) {
      setError(t('contactForm.errorRequired'));
      setIsSubmitting(false);
      return;
    }

    try {
      // 1. Salvar no Supabase (leads) - apenas se não for email de admin
      const isAdminEmail = ADMIN_EMAILS.includes(formData.email.toLowerCase());

      if (!isAdminEmail) {
        const { error: supabaseError } = await supabase
          .from('leads')
          .insert([{
            name: formData.nome,
            email: formData.email,
            phone: formData.telefone,
            company: formData.empresa || null,
            message: formData.mensagem || null,
            source: window.location.hostname,
            status: 'new'
          }]);

        if (supabaseError) {
          console.error('Erro Supabase:', supabaseError);
          // Continua mesmo se falhar no Supabase (não bloqueia o usuário)
        }
      }

      // 2. Enviar para HubSpot
      const hubspotData = {
        fields: [
          { name: 'firstname', value: formData.nome },
          { name: 'email', value: formData.email },
          { name: 'phone', value: formData.telefone },
          { name: 'company', value: formData.empresa },
          { name: 'mensagem_formulario', value: formData.mensagem }
        ],
        context: {
          pageUri: window.location.href,
          pageName: 'Vega Robotics - Home'
        }
      };

      const response = await fetch(
        'https://api.hsforms.com/submissions/v3/integration/submit/50764884/75e376ed-bef0-449d-a656-2ee74aa74ba0',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(hubspotData)
        }
      );

      if (response.ok) {
        setIsSuccess(true);
      } else {
        throw new Error('Erro ao enviar formulário');
      }
    } catch (err) {
      console.error('Erro:', err);
      setError(t('contactForm.errorGeneral'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <Modal>
        <ModalHeader>
          <ModalTitle>{t('contactForm.title')} <span>{t('contactForm.titleHighlight')}</span></ModalTitle>
          <CloseButton onClick={onClose} aria-label={t('general.close')}>
            <FaTimes />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {isSuccess ? (
            <SuccessMessage>
              <SuccessIcon>
                <FaCheck />
              </SuccessIcon>
              <SuccessTitle>{t('contactForm.successTitle')}</SuccessTitle>
              <SuccessText>{t('contactForm.successText')}</SuccessText>
              <SubmitButton onClick={onClose} style={{ marginTop: '1rem' }}>
                {t('general.close')}
              </SubmitButton>
            </SuccessMessage>
          ) : (
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label><FaUser /> {t('contactForm.name')} *</Label>
                <Input
                  type="text"
                  name="nome"
                  placeholder={t('contactForm.namePlaceholder')}
                  value={formData.nome}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label><FaEnvelope /> {t('contactForm.email')} *</Label>
                <Input
                  type="email"
                  name="email"
                  placeholder={t('contactForm.emailPlaceholder')}
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label><FaPhone /> {t('contactForm.phone')} *</Label>
                <Input
                  type="tel"
                  name="telefone"
                  placeholder={t('contactForm.phonePlaceholder')}
                  value={formData.telefone}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label><FaBuilding /> {t('contactForm.company')}</Label>
                <Input
                  type="text"
                  name="empresa"
                  placeholder={t('contactForm.companyPlaceholder')}
                  value={formData.empresa}
                  onChange={handleChange}
                />
              </FormGroup>

              <FormGroup>
                <Label><FaFileAlt /> {t('contactForm.message')}</Label>
                <TextArea
                  name="mensagem"
                  placeholder={t('contactForm.messagePlaceholder')}
                  value={formData.mensagem}
                  onChange={handleChange}
                />
              </FormGroup>

              {error && <ErrorMessage>{error}</ErrorMessage>}

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Spinner />
                ) : (
                  <>
                    <FaPaperPlane /> {t('contactForm.submit')}
                  </>
                )}
              </SubmitButton>

              <PrivacyText>{t('contactForm.privacy')}</PrivacyText>
            </Form>
          )}
        </ModalBody>
      </Modal>
    </Overlay>
  );
};

export default ContactFormModal;
